// FLSSafetySystemsTable.tsx
import React from 'react';

export interface FLSSafetySystemRow {
  srNo: number;
  particulars: string;
  installed: number | null;
  working: number | null;
  notWorking: number | null;
  lastInspectionDate: string | null;
  remarks: string | null;
}

// Sr.Nos linked to KPI Fire Safety Scorecard parameters (must match syncMISToKPI.ts)
const KPI_LINKED_SR_NOS: number[] = [1, 3, 5, 6, 8, 11, 12, 14, 16, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35];

interface FLSSafetySystemsTableProps {
  data?: FLSSafetySystemRow[];
  onChange?: (index: number, updatedRow: FLSSafetySystemRow) => void;
  isEditable?: boolean;
}

export default function FLSSafetySystemsTable({
  data = [],
  onChange,
  isEditable = true,
}: FLSSafetySystemsTableProps) {
  const handleFieldChange = (
    idx: number,
    field: keyof FLSSafetySystemRow,
    value: string | number | null
  ) => {
    if (!onChange || !isEditable) return;

    const row = data[idx];
    let processedValue: string | number | null = value;

    // Handle numeric fields
    if (field === 'installed' || field === 'working') {
      if (typeof value === 'string') {
        if (value.toLowerCase() === 'na' || value.toLowerCase() === 'n/a' || value === '') {
          processedValue = null;
        } else {
          const numValue = parseFloat(value);
          processedValue = isNaN(numValue) ? null : numValue;
        }
      }
    }

    // Prevent editing of non-editable fields
    if (field === 'srNo' || field === 'particulars' || field === 'notWorking') {
      return;
    }

    let updatedRow = { ...row, [field]: processedValue };

    // Auto-calculate notWorking = installed - working
    if (field === 'installed' || field === 'working') {
      const installed = updatedRow.installed !== null ? Number(updatedRow.installed) : null;
      let working = updatedRow.working !== null ? Number(updatedRow.working) : null;

      // Validate and auto-adjust working (inspected) if it exceeds installed
      if (installed !== null && working !== null) {
        if (working > installed) {
          alert('Inspected & Healthy Working (In Nos.) cannot exceed Installed (In Nos.). Value has been adjusted to match Installed.');
          working = installed; // Auto-adjust to installed value
          updatedRow.working = working;
        }
        updatedRow.notWorking = Math.max(0, installed - working);
      } else if (installed !== null && working === null) {
        updatedRow.notWorking = installed;
      } else {
        updatedRow.notWorking = null;
      }
    }

    onChange(idx, updatedRow);
  };

  const formatValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value.toString();
  };

  const getDisplayValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value.toString();
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="bg-blue-600 text-white px-4 py-2 font-bold text-lg mb-0 rounded-t-lg">
        (I) Fire and Life safety Equipment
      </div>
      <table className="min-w-full border-collapse border border-gray-300 bg-white">
        <thead>
          <tr className="bg-gray-200 border-b-2 border-gray-400">
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-16">
              Sr.No
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 min-w-[300px]">
              Particulars
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-32">
              Installed (In Nos.)
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-40">
              Inspected & Healthy Working (In Nos.)
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-32">
              Not working (In Nos.)
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-40">
              Last Date of inspection
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 min-w-[250px]">
              Remarks for not Installed / not Working
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => {
            const isLinked = KPI_LINKED_SR_NOS.includes(row.srNo);
            return (
              <tr
                key={idx}
                className={`transition-colors ${isLinked
                  ? 'bg-teal-50 hover:bg-teal-100 border-l-4 border-l-teal-500'
                  : 'bg-white hover:bg-gray-50'
                  }`}
                title={isLinked ? 'Linked to KPI Fire Safety Scorecard' : undefined}
              >
                <td className={`border border-gray-300 px-3 py-2 text-center text-sm font-medium ${isLinked ? 'text-teal-800' : 'text-gray-700'
                  }`}>
                  {isLinked && <span className="inline-block w-2 h-2 rounded-full bg-teal-500 mr-1" />}
                  {row.srNo}
                </td>
                <td className="border border-gray-300 px-4 py-2 text-sm text-gray-700">
                  <span className="whitespace-pre-wrap break-words">{row.particulars || ''}</span>
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  {isEditable ? (
                    <input
                      type="number"
                      min="0"
                      value={getDisplayValue(row.installed)}
                      onChange={(e) => handleFieldChange(idx, 'installed', e.target.value)}
                      placeholder="N/A"
                      className="w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <span className="text-sm text-gray-700 font-medium">
                      {row.installed !== null && row.installed !== undefined ? row.installed : 'N/A'}
                    </span>
                  )}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  {isEditable ? (
                    <input
                      type="number"
                      min="0"
                      value={getDisplayValue(row.working)}
                      onChange={(e) => handleFieldChange(idx, 'working', e.target.value)}
                      placeholder="N/A"
                      disabled={row.installed === null || row.installed === undefined}
                      className={`w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${(row.installed === null || row.installed === undefined)
                        ? 'bg-gray-100 cursor-not-allowed opacity-60'
                        : ''
                        }`}
                    />
                  ) : (
                    <span className="text-sm text-gray-700 font-medium">
                      {row.working !== null && row.working !== undefined ? row.working : 'N/A'}
                    </span>
                  )}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  <span className="text-sm text-gray-700 font-medium">
                    {row.notWorking !== null && row.notWorking !== undefined ? row.notWorking : 'N/A'}
                  </span>
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  {isEditable ? (
                    <input
                      type="date"
                      value={row.lastInspectionDate || ''}
                      onChange={(e) => handleFieldChange(idx, 'lastInspectionDate', e.target.value || null)}
                      className="w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  ) : (
                    <span className="text-sm text-gray-700 font-medium">
                      {row.lastInspectionDate || 'N/A'}
                    </span>
                  )}
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  {(() => {
                    const requiresRemarks = row.notWorking !== null && row.notWorking !== undefined && row.notWorking > 0;
                    const isMissing = requiresRemarks && !row.remarks?.trim();
                    return isEditable ? (
                      <div>
                        <textarea
                          value={row.remarks || ''}
                          onChange={(e) => handleFieldChange(idx, 'remarks', e.target.value || null)}
                          placeholder={requiresRemarks ? 'Remarks required *' : 'Enter remarks'}
                          rows={2}
                          className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:border-transparent resize-y ${isMissing
                            ? 'border-red-400 bg-red-50 focus:ring-red-500 placeholder-red-400'
                            : 'border-gray-400 bg-white focus:ring-blue-500'
                            }`}
                        />
                        {isMissing && (
                          <p className="text-xs text-red-500 mt-1">Remarks required for not working items</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-700 whitespace-pre-wrap break-words">{row.remarks || 'N/A'}</span>
                    );
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

